import { create } from 'zustand'
import { useLogin } from '@api/login/state'
import { reportUnexpectedClientError } from '@services/client-error.service'
import { getAvatarCatalog } from './api'
import type { AvatarCatalogItem, AvatarFolder } from './types'

const identity = () => {
    const state = useLogin.getState()
    if (!state.isAuthorized) return ''
    if (state.profile.loginName) return state.profile.loginName
    try { return localStorage.getItem('us_id') || '' } catch { return '' }
}
let requestVersion = 0
let inFlight: {identity: string; promise: Promise<boolean>} | undefined
type CatalogState = {
    items: AvatarCatalogItem[]; folders: AvatarFolder[]; canManage: boolean; canEdit: boolean; canDelete: boolean
    isLoading: boolean; isLoaded: boolean; loadedFor: string; error: string
    loadCatalog: (force?: boolean) => Promise<boolean>
}
export const useAvatarCatalog = create<CatalogState>((set, get) => ({
    items: [], folders: [], canManage: false, canEdit: false, canDelete: false, isLoading: false, isLoaded: false, loadedFor: '', error: '',
    loadCatalog: (force = false) => {
        const owner = identity()
        if (!owner) return Promise.resolve(false)
        if (inFlight?.identity === owner) return inFlight.promise
        if (!force && get().isLoaded && get().loadedFor === owner) return Promise.resolve(true)
        const version = ++requestVersion
        set({isLoading: true, error: '', ...(get().loadedFor !== owner ? {items: [], folders: [], canManage: false, canEdit: false, canDelete: false, isLoaded: false} : {})})
        const pending = (async () => {
            try {
                const response = await getAvatarCatalog()
                if (identity() !== owner || version !== requestVersion) return false
                if (response.status !== 200 || !response.data) {
                    set({...(response.status === 401 || response.status === 403 ? {canManage: false, canEdit: false, canDelete: false} : {}), error: response.message || 'Не удалось загрузить каталог аватаров.'})
                    return false
                }
                const {items, folders, canManage, canEdit, canDelete} = response.data
                if (!Array.isArray(items) || !Array.isArray(folders)) throw new Error('Invalid avatar catalog response')
                set({items, folders, canManage: canManage === true, canEdit: canManage === true && canEdit === true, canDelete: canManage === true && canDelete === true, isLoaded: true, loadedFor: owner})
                return true
            } catch (failure) {
                if (identity() === owner && version === requestVersion) {
                    reportUnexpectedClientError(failure, 'AvatarCatalog.load')
                    set({error: 'Не удалось загрузить каталог аватаров. Попробуйте ещё раз.'})
                }
                return false
            } finally {
                if (version === requestVersion) set({isLoading: false})
            }
        })()
        inFlight = {identity: owner, promise: pending}
        void pending.then(() => {if (inFlight?.promise === pending) inFlight = undefined})
        return pending
    },
}))
